import { useTranslation } from 'react-i18next'
import './styles.scss'
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import RoomRow from './RoomRow'
import activityTrackingService from '../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../constants'
import DeleteRoomModal from '../DeleteRoomModal'
import PageHead from '../../../../Shared/PageHead'
import Button from '../../../../Shared/Form/Button'
import PublishAllButton from '../../PublishAllButton'
import Toggle from '../../../../Shared/Toggle'
import LayerEditImage from '../../ImageBrowser/Layer/layerEditImage'
import { useSelector } from 'react-redux'
import { isLowResolutionImage } from '../../../../../services/lowResolutionImageRules'

const RoomList = ({
                    property,
                    rooms,
                    roomAttributes,
                    images,
                    onCreateRoomClick,
                    onRoomSelected,
                    onRoomRemoved,
                    onImagesChange,
                    hidden,
                    uploadQueue,
                    onUploadFiles,
                    onUploadQueueImageRemove,
                    galleries = {},
                    onGalleriesChange,
                    onImagesDeleted,
                  }) => {
  const { t } = useTranslation()
  const [showCodes, setShowCodes] = useState(false)
  const [expandedRoomsMap, setExpandedRoomsMap] = useState(new Map(rooms.map((room, index) => [room.id, index === 0])))
  const [isAllExpanded, setIsAllExpanded] = useState(false)
  const [roomToDelete, setRoomToDelete] = useState(null)
  const [openMenuImageId, setOpenMenuImageId] = useState(null)
  const [openMenuRoomId, setOpenMenuRoomId] = useState(null)
  const [imageUnderEdit, setImageUnderEdit] = useState(null)
  const permissions = useSelector((state) => state.property.permissions)
  const location = useLocation()
  const navigate = useNavigate()
  const pendingScrollRoomIdRef = useRef(null)

  const buildRoomImagesMap = useCallback(() => {
    const imagesMap = images.reduce((map, image) => {
      map[image.id] = image
      return map
    }, {})
    const roomImagesMap = {}
    for (const roomId of Object.keys(galleries.rooms ?? {})) {
      const imageIds = galleries.rooms[roomId].order ?? []
      for (const imageId of imageIds) {
        const image = imagesMap[imageId]
        if (!image) {
          continue
        }
        roomImagesMap[roomId] ??= []
        roomImagesMap[roomId].push(image)
      }
    }
    return roomImagesMap
  }, [images, galleries])
  const [roomImagesMap, setRoomImagesMap] = useState(() => buildRoomImagesMap())

  const onShowCodesToggle = () => {
    activityTrackingService.trackEvent(trackingEvent.clickShowCodes)
    setShowCodes((prevShowCodes) => !prevShowCodes)
  }

  const onExpandToggle = (room) => {
    setExpandedRoomsMap((prevExpandedRoomsMap) => {
      const newExpandedRoomsMap = new Map(prevExpandedRoomsMap)
      newExpandedRoomsMap.set(room.id, !newExpandedRoomsMap.get(room.id))
      return newExpandedRoomsMap
    })
  }

  const onExpandAllToggle = () => {
    setExpandedRoomsMap((prevExpandedRoomsMap) => {
      const newExpandedRoomsMap = new Map()
      rooms.forEach((room) => {
        newExpandedRoomsMap.set(room.id, !isAllExpanded)
      })
      setIsAllExpanded((prevIsAllExpanded) => !prevIsAllExpanded)
      return newExpandedRoomsMap
    })
  }

  const cancelRoomDelete = () => {
    setRoomToDelete(null)
  }

  const onRoomDeleteClick = (room) => {
    setRoomToDelete(room)
  }

  useEffect(() => {
    setRoomImagesMap(
      buildRoomImagesMap()
    )
  }, [buildRoomImagesMap])

  // the low-resolution-images notification links here with this state: collapse
  // all rooms and expand the first one holding a low-res image. the state is
  // consumed once applied, so later image/gallery changes, a reload or coming
  // back to this history entry don't re-collapse the rooms; it waits for the
  // images/galleries when they aren't loaded yet.
  useEffect(() => {
    if (!location.state?.expandLowResolutionRoom) {
      return
    }
    const room = rooms.find((r) => (roomImagesMap[r.id] ?? []).some(isLowResolutionImage))
    if (!room) {
      return
    }
    pendingScrollRoomIdRef.current = room.id
    setExpandedRoomsMap(new Map(rooms.map((r) => [r.id, r.id === room.id])))
    setIsAllExpanded(false)
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null })
  }, [location, navigate, rooms, roomImagesMap])

  // scroll only once the collapse/expand above is committed, otherwise rooms
  // above collapsing after the scroll started would shift the target room.
  useLayoutEffect(() => {
    if (pendingScrollRoomIdRef.current === null) {
      return
    }
    const roomId = pendingScrollRoomIdRef.current
    pendingScrollRoomIdRef.current = null
    document.getElementById(`room-list-room-${roomId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [expandedRoomsMap])

  const onImageChanged = (image) => {
    const newImages = [...images]
    const index = newImages.findIndex((i) => i.id === image.id)
    if (index !== -1) {
      newImages[index] = image
      onImagesChange(newImages)
    }
  }

  if (hidden) {
    return null
  }
  return (
    <div className={'room-list-container'}>
      <LayerEditImage image={imageUnderEdit} show={!!imageUnderEdit}
                      onHideClick={() => setImageUnderEdit(null)}
                      onImageChanged={onImageChanged}
                      permissions={permissions}
                      rooms={rooms} roomAttributes={roomAttributes}
                      galleries={galleries}
                      onGalleriesChange={onGalleriesChange}
      />
      <div className={'room-list'}>
        <div className={'room-list-header-container'}>
          <PageHead
            title={t('ROOM_TYPES')}
            subtitle={t('ROOM_LIST_DESCRIPTION')}
          >
            <Button
              onClick={onCreateRoomClick}
              variant="secondary"
            >
              {t('CREATE_ROOM')}
            </Button>
            <PublishAllButton giataId={property.giataId}/>
          </PageHead>
        </div>
        {
          rooms.length > 0 && <div className={'room-list-content-container'}>
            <div className={'room-list-content-header'}>
              <span
                className={'room-list-expand-all'}
                onClick={onExpandAllToggle}
              >
                {isAllExpanded ? t('COLLAPSE_ALL') : t('EXPAND_ALL')}
              </span>
              <Toggle checked={showCodes} onChange={onShowCodesToggle} labelText={t('SHOW_CODES')}/>
            </div>
            <div className={'room-list-content'}>
              {
                rooms.map(
                  (room) => <RoomRow
                    key={room.id}
                    room={room}
                    roomAttributes={roomAttributes}
                    roomImages={roomImagesMap[room.id] ?? []}
                    gallery={galleries.rooms?.[room.id] ?? {}}
                    galleries={galleries}
                    onGalleriesChange={onGalleriesChange}
                    showCodes={showCodes}
                    isExpanded={expandedRoomsMap.get(room.id) ?? false}
                    onExpandToggle={onExpandToggle}
                    onRoomSelected={onRoomSelected}
                    onRoomDeleteClick={onRoomDeleteClick}
                    uploadQueue={uploadQueue.filter(item => item?.roomId === room.id)}
                    onUploadFiles={onUploadFiles}
                    onUploadQueueImageRemove={onUploadQueueImageRemove}
                    onImagesDeleted={onImagesDeleted}
                    openMenuImageId={openMenuImageId}
                    onMenuOpenChange={(imageId, roomId) => {
                      setOpenMenuImageId(imageId)
                      setOpenMenuRoomId(roomId)
                    }}
                    openMenuRoomId={openMenuRoomId}
                    onImageUnderEditChange={setImageUnderEdit}
                  />
                )
              }
            </div>
          </div>
        }
      </div>
      {
        roomToDelete &&
        <DeleteRoomModal show={true}
                         giataId={property.giataId}
                         room={roomToDelete}
                         images={images}
                         galleries={galleries}
                         roomAttributes={roomAttributes}
                         onEscapeKeyDown={cancelRoomDelete}
                         onBackdropClick={cancelRoomDelete}
                         onRoomRemoved={(room) => {
                           onRoomRemoved(room)
                           setRoomToDelete(null)
                         }}
                         onCancel={cancelRoomDelete}
                         onGalleriesChange={onGalleriesChange}
        />
      }
    </div>
  )
}

export default RoomList
